import { Upload } from "lucide-react";
import { useTranslation } from "react-i18next";
import { buttonVariants } from "@/components/ui/button-variants";
import { HoverPopover } from "@/components/ui/HoverPopover";
import { FILE_ACCEPT } from "@/constants/fileAccept";
import { isFutureVisitAtIso } from "@/components/handshake/visitAt";
import { validateReceiptFileOrToast } from "@/lib/fileValidation";
import { FileDropzone } from "@/components/common/FileDropzone";
import { cn } from "@/lib/utils";

interface ReceiptUploadButtonProps {
  bookingId: string;
  isProcessing: boolean;
  fileInputResetKey: number;
  onFileSelect: (file: File) => void;
  /** Primary full-width CTA (e.g. booking detail actions). Default is compact inline. */
  fullWidth?: boolean;
  /** Booking visit_at ISO; upload stays locked until this instant. */
  visitAt?: string;
}

export function ReceiptUploadButton({
  bookingId,
  isProcessing,
  fileInputResetKey,
  onFileSelect,
  fullWidth = false,
  visitAt,
}: ReceiptUploadButtonProps) {
  const { t } = useTranslation("components");
  const lockedUntilVisit = Boolean(visitAt && isFutureVisitAtIso(visitAt));
  const disabled = isProcessing || lockedUntilVisit;
  const label = isProcessing ? t("receiptUploadAnalyzing") : t("receiptUploadLabel");
  const primaryCtaClass =
    "border-transparent text-white hover:border-transparent hover:text-white";

  const handleFile = (file: File) => {
    if (lockedUntilVisit) {
      return;
    }
    if (
      !validateReceiptFileOrToast(file, {
        tooLarge: (max) => t("receiptUploadTooLarge", { max }),
        unsupported: t("receiptUploadUnsupported"),
      })
    ) {
      return;
    }
    onFileSelect(file);
  };

  const dropzone = (
    <FileDropzone
      id={`receipt-upload-${bookingId}-${fileInputResetKey}`}
      accept={FILE_ACCEPT.imageAndPdf}
      disabled={disabled}
      isLoading={isProcessing}
      layout="inline"
      spinnerSize="sm"
      icon={Upload}
      iconClassName={cn(
        "shrink-0 text-white",
        fullWidth ? "h-4 w-4" : "h-3.5 w-3.5",
      )}
      title={label}
      titleClassName={cn("text-white", fullWidth ? "text-sm" : "text-[13px]")}
      className={cn(
        "whitespace-nowrap border-solid text-center",
        fullWidth
          ? cn(
              buttonVariants({ variant: "primary", size: "lg" }),
              primaryCtaClass,
              "w-full justify-center sm:w-auto",
            )
          : cn(buttonVariants({ variant: "primary", size: "sm" }), primaryCtaClass),
        lockedUntilVisit && "opacity-50",
      )}
      onFilesSelected={(files) => {
        const file = files[0];
        if (file) {
          handleFile(file);
        }
      }}
    />
  );

  if (!lockedUntilVisit) {
    return dropzone;
  }

  return (
    <HoverPopover content={t("receiptUploadBeforeVisit")} placement="above">
      <span
        className={cn(
          "inline-flex cursor-default",
          fullWidth && "w-full sm:w-auto",
        )}
        tabIndex={0}
      >
        {dropzone}
      </span>
    </HoverPopover>
  );
}
